import { initReactI18next } from 'react-i18next';

import i18next, { type i18n } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import { catalogToResources } from './catalog';
import {
	FALLBACK_LOCALE,
	LNG_STORAGE_KEY,
	normalizeLocale,
	SUPPORTED_LOCALES,
} from './config';
import { EN_FALLBACK, NAMESPACES } from './en.fallback';
import { fetchCatalog } from './fetchCatalog';

export type CreateI18nOptions = {
	baseUrl: string;
	enterpriseId?: string | undefined;
	initialLocale?: string | undefined;
	// Returns the host app's current Bearer access token (or null/undefined when
	// unauthenticated). Called on every catalog load so a language switch after
	// login sends fresh credentials to the auth-gated catalog endpoint.
	getToken?: (() => string | null | undefined) | undefined;
};

export type CreateI18nResult = {
	i18n: i18n;
	loadLocale: (locale: string) => Promise<void>;
};

export const createI18n = (opts: CreateI18nOptions): CreateI18nResult => {
	const instance = i18next.createInstance();

	// Keep the init promise: the initial loadLocale below MUST wait for it.
	// `init()` resolves language detection (localStorage -> navigator -> htmlTag);
	// reading `instance.resolvedLanguage` before it settles yields undefined.
	const ready = instance
		.use(LanguageDetector)
		.use(initReactI18next)
		.init({
			resources: catalogToResources('en', EN_FALLBACK),
			fallbackLng: FALLBACK_LOCALE,
			supportedLngs: [...SUPPORTED_LOCALES],
			// Strip the region subtag so a `ja-JP` browser resolves to the `ja`
			// catalog (rows are keyed by base language). Without this, ja-JP
			// users fall back to English despite Japanese translations existing.
			load: 'languageOnly',
			ns: NAMESPACES,
			defaultNS: 'served_view',
			...(opts.initialLocale ? { lng: opts.initialLocale } : {}),
			detection: {
				order: ['localStorage', 'navigator', 'htmlTag'],
				caches: ['localStorage'],
				lookupLocalStorage: LNG_STORAGE_KEY,
			},
			// `loadLocale` injects the new-locale catalog via `addResourceBundle`
			// (an i18next *store* event), then flips the language. Without store
			// binding, only `changeLanguage` -> `languageChanged` forces mounted
			// `useTranslation` consumers to re-render, so any string whose bundle
			// lands around/after that event stays in the old language until a full
			// remount (F5). Binding the store `added`/`loaded` events makes those
			// late-arriving bundles re-render live — fixing the "~7% only flips
			// after refresh" gap on language switch. See PAAS-733.
			react: {
				useSuspense: false,
				bindI18n: 'languageChanged loaded',
				bindI18nStore: 'added removed',
			},
			interpolation: { escapeValue: false },
		});

	// Set as soon as anything explicitly asks for a locale, so the deferred
	// startup load below never overrides a caller's choice that landed first.
	let explicitlyLoaded = false;

	const loadLocale = async (requestedLocale: string): Promise<void> => {
		explicitlyLoaded = true;
		// Canonicalize (ja-JP → ja) so the fetch, resource bundle, and active
		// language all key off the base language the catalog is stored under.
		const locale = normalizeLocale(requestedLocale);
		// No catalog host yet (host shell mounts before auth populates baseApiUrl,
		// or a test harness renders with none). Without this guard fetchCatalog
		// builds the RELATIVE url `/api/translations/catalog`, which resolves
		// against whatever origin is serving the SPA — the dev server, or on a
		// deployed host some unrelated service — and the HTML it returns fails
		// JSON parsing. Keep the bundled EN fallback instead of fetching.
		if (!opts.baseUrl) {
			return;
		}
		try {
			const { catalog } = await fetchCatalog({
				baseUrl: opts.baseUrl,
				locale,
				enterpriseId: opts.enterpriseId,
				token: opts.getToken?.() ?? undefined,
			});
			for (const ns of Object.keys(catalog)) {
				instance.addResourceBundle(locale, ns, catalog[ns], true, true);
			}
			await instance.changeLanguage(locale);
		} catch (e) {
			console.warn('i18n catalog load failed', e);
		}
	};

	// Wait for init so detection (localStorage -> navigator -> htmlTag) has settled.
	//
	// Use `language`, NOT `resolvedLanguage`. `language` is what the user actually
	// chose/was detected; `resolvedLanguage` is the value AFTER fallback, and at
	// init no `ja` bundle exists yet (only the embedded `en` fallback is in
	// `resources`), so it reports 'en' even when `language` is 'ja':
	//
	//     at ready -> language: ja | resolvedLanguage: en | storage: ja
	//
	// Loading from `resolvedLanguage` therefore fetched the English catalog and
	// called changeLanguage('en'), and LanguageDetector's `caches: ['localStorage']`
	// wrote that back — silently clobbering the user's saved 'ja' on every refresh.
	void ready.then(() => {
		// A caller (e.g. the language switcher, or a host restoring state) may have
		// already loaded a locale while init was still settling. Don't undo it.
		if (explicitlyLoaded) return undefined;
		return loadLocale(
			instance.language ?? instance.resolvedLanguage ?? FALLBACK_LOCALE,
		);
	});

	return { i18n: instance, loadLocale };
};
